import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../services/firebase';
import imageCompression from 'browser-image-compression';

interface MediaUploadProps {
  currentMediaUrl?: string;
  currentMediaType?: 'image' | 'video';
  onMediaUploaded: (url: string, type: 'image' | 'video') => void;
  onMediaDeleted: () => void;
  storagePath: string;
  label?: string;
  maxSizeMB?: number;
  allowedTypes?: ('image' | 'video')[];
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  currentMediaUrl,
  currentMediaType,
  onMediaUploaded,
  onMediaDeleted,
  storagePath,
  label = 'メディア',
  maxSizeMB = 50, // 動画用に50MBに拡大
  allowedTypes = ['image', 'video'],
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'image' | 'video'>(
    allowedTypes.length === 1 ? allowedTypes[0] : 'image'
  );

  const getAcceptedFormats = () => {
    const formats: string[] = [];
    if (allowedTypes.includes('image')) {
      formats.push('image/*');
    }
    if (allowedTypes.includes('video')) {
      formats.push('video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm');
    }
    return formats.join(',');
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルタイプチェック
    let fileType: 'image' | 'video' | null = null;
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
    }

    if (!fileType || !allowedTypes.includes(fileType)) {
      setError('対応していないファイル形式です');
      return;
    }

    setError(null);
    setUploading(true);

    try {
      let fileToUpload = file;

      // 画像の場合は圧縮
      if (fileType === 'image') {
        const options = {
          maxSizeMB: 0.5, // 最大500KB（モバイルオーダー用に軽量化）
          maxWidthOrHeight: 1024, // 最大1024px
          useWebWorker: true,
          fileType: 'image/jpeg', // JPEGに統一
        };

        try {
          const compressedFile = await imageCompression(file, options);
          fileToUpload = compressedFile;
          console.log('圧縮完了:', {
            元のサイズ: (file.size / 1024).toFixed(2) + 'KB',
            圧縮後: (compressedFile.size / 1024).toFixed(2) + 'KB',
          });
        } catch (compressionError) {
          console.error('圧縮エラー（元ファイルを使用）:', compressionError);
          // 圧縮失敗時は元ファイルをそのまま使用
        }
      }

      // 動画の場合も簡易チェック（圧縮は行わないが、サイズ制限は確認）
      if (fileType === 'video') {
        const maxVideoSizeBytes = 10 * 1024 * 1024; // 動画は10MBまで
        if (file.size > maxVideoSizeBytes) {
          setError('動画ファイルは10MB以下にしてください');
          setUploading(false);
          return;
        }
      }

      // ファイル名を生成（タイムスタンプ + オリジナルファイル名）
      const timestamp = Date.now();
      const extension = fileType === 'image' ? 'jpg' : file.name.split('.').pop();
      const filename = `${timestamp}.${extension}`;
      const storageRef = ref(storage, `${storagePath}/${filename}`);

      // アップロード
      await uploadBytes(storageRef, fileToUpload);

      // ダウンロードURLを取得
      const downloadURL = await getDownloadURL(storageRef);

      onMediaUploaded(downloadURL, fileType);
    } catch (err) {
      console.error('Upload error:', err);
      setError('メディアのアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentMediaUrl) return;

    if (!window.confirm('メディアを削除してもよろしいですか？')) {
      return;
    }

    try {
      // URLからStorage参照を作成
      const mediaRef = ref(storage, currentMediaUrl);
      await deleteObject(mediaRef);
      onMediaDeleted();
    } catch (err) {
      console.error('Delete error:', err);
      setError('メディアの削除に失敗しました');
    }
  };

  const renderMediaPreview = () => {
    if (!currentMediaUrl) return null;

    if (currentMediaType === 'video') {
      return (
        <Box>
          <Box
            component="video"
            src={currentMediaUrl}
            controls
            sx={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
              borderRadius: 1,
              mb: 1,
            }}
          />
          <Box>
            <IconButton
              onClick={handleDelete}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      );
    }

    // 画像の場合
    return (
      <Box>
        <Box
          component="img"
          src={currentMediaUrl}
          alt={label}
          sx={{
            width: '100%',
            maxWidth: 300,
            height: 'auto',
            borderRadius: 1,
            mb: 1,
          }}
        />
        <Box>
          <IconButton
            onClick={handleDelete}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        {label}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {currentMediaUrl ? (
        renderMediaPreview()
      ) : (
        <Box>
          {allowedTypes.length > 1 && (
            <ToggleButtonGroup
              value={selectedType}
              exclusive
              onChange={(_, newType) => {
                if (newType) setSelectedType(newType);
              }}
              size="small"
              sx={{ mb: 2 }}
            >
              {allowedTypes.includes('image') && (
                <ToggleButton value="image">
                  <ImageIcon sx={{ mr: 1 }} />
                  画像
                </ToggleButton>
              )}
              {allowedTypes.includes('video') && (
                <ToggleButton value="video">
                  <VideoIcon sx={{ mr: 1 }} />
                  動画
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          )}

          <input
            accept={getAcceptedFormats()}
            style={{ display: 'none' }}
            id={`media-upload-${storagePath}`}
            type="file"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor={`media-upload-${storagePath}`}>
            <Button
              variant="outlined"
              component="span"
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              disabled={uploading}
            >
              {uploading ? 'アップロード中...' : `${selectedType === 'video' ? '動画' : '画像'}をアップロード`}
            </Button>
          </label>
          <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
            {selectedType === 'video'
              ? `最大${maxSizeMB}MB、MP4/MOV/AVI/WebM形式`
              : `最大${maxSizeMB}MB、JPG/PNG形式`
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MediaUpload;
